import { ChannelMediaEditor } from "./channel-media-editor";
import { Button } from "@ondit/ui";
import type { MediaRule } from "@ondit/publishing";
import { Link } from "react-router";
import { fileUrl } from "~/common/media/api";
import { pick, useI18n } from "~/core/i18n";
export function SourceMediaField({
	brandId,
	onBusyChange,
	assets,
	value,
	rule,
	disabled,
	editUrl,
	onChange,
	onEditMaterial,
}: {
	brandId?: string;
	onBusyChange?: (busy: boolean) => void;
	assets: { file_id: string; url: string; type: string; mime: string }[];
	value: { url: string; type?: string; mime?: string }[];
	rule: MediaRule;
	disabled: boolean;
	editUrl: string;
	onEditMaterial?: () => void;
	onChange: (value: { url: string; type?: string; mime?: string }[]) => void;
}) {
	const { locale } = useI18n();
	const available = assets.filter(
		(a) =>
			rule.accept
				.split(",")
				.some(
					(mime) =>
						a.mime === mime.trim() || (mime.trim().endsWith("/*") && a.mime.startsWith(mime.trim().slice(0, -1))),
				) && !(rule.no_video && a.type === "video"),
	);
	const mediaLabel =
		rule.no_video || !rule.accept.includes("video")
			? pick(locale, ["Images", "图片"])
			: pick(locale, ["Images / videos", "图片 / 视频"]);
	const max = rule.multiple ? (rule.max_count ?? 100) : 1;
	if (brandId)
		return (
			<div className="smart-image-field smart-field-body">
				<strong>
					{mediaLabel}
					{rule.required ? " *" : ""} · {pick(locale, [`${value.length} selected`, `已选 ${value.length} 项`])}
				</strong>
				<ChannelMediaEditor
					brandId={brandId}
					assets={assets}
					value={value}
					onChange={(items) => {
						if (!rule.video_alone) return onChange(items);
						const added = items.filter((item) => !value.some((old) => old.url === item.url));
						const video = added.find((item) => item.type === "video");
						onChange(video ? [video] : added.length ? items.filter((item) => item.type !== "video") : items);
					}}
					disabled={disabled}
					accept={
						rule.no_video
							? rule.accept
									.split(",")
									.filter((v) => !v.trim().startsWith("video/"))
									.join(",")
							: rule.accept
					}
					maxCount={max}
					onBusyChange={onBusyChange}
				/>
			</div>
		);
	return (
		<div className="smart-image-field smart-field-body">
			<strong>
				{mediaLabel}
				{rule.required ? " *" : ""} · {pick(locale, [`${value.length} selected`, `已选 ${value.length} 项`])}
			</strong>
			<div className="smart-image-options" role="group" aria-label={pick(locale, ["Select media", "选择图片或视频"])}>
				{available.map((asset, index) => {
					const selected = value.some((m) => m.url === asset.url);
					return (
						<Button
							key={asset.file_id}
							disabled={
								disabled ||
								(!selected &&
									rule.multiple &&
									value.length >= max &&
									!(rule.video_alone && (asset.type === "video" || value.some((m) => m.type === "video"))))
							}
							variant={selected ? "outline" : "ghost"}
							aria-pressed={selected}
							aria-label={pick(locale, [`Select media ${index + 1}`, `选择素材 ${index + 1}`])}
							onClick={() => {
								if (selected) onChange(value.filter((m) => m.url !== asset.url));
								else {
									const existing =
										rule.video_alone && (asset.type === "video" || value.some((m) => m.type === "video"))
											? []
											: rule.multiple
												? value
												: [];
									onChange([...existing, { url: asset.url, type: asset.type, mime: asset.mime }]);
								}
							}}
						>
							{asset.type === "image" ? (
								<img src={fileUrl(asset.file_id)} alt="" />
							) : (
								<span>{pick(locale, ["Video", "视频"])}</span>
							)}
						</Button>
					);
				})}
			</div>
			{!available.length && (
				<p className="hint">
					{pick(locale, [
						"No suitable media in the source materials. Add images or videos, save, and start a new distribution plan.",
						"素材中暂无符合要求的图片或视频。添加并保存素材后，请新建分发计划。",
					])}
				</p>
			)}
			<Button asChild variant="outline">
				<Link to={editUrl} onClick={onEditMaterial}>
					{pick(locale, ["Edit article material", "编辑文章素材"])}
				</Link>
			</Button>
		</div>
	);
}
